import { DialogOptions } from '../../../models/modal.model'
import {
  GenericNotificationCode,
  NotificationOptions,
  parseGenericNotification,
} from '../../../models/notification.model'
import { Order } from '../models/order.entity'

export type OrderNotificationCode =
  | GenericNotificationCode
  | 'ADDRESS_UPDATED'
  | 'ATTACHMENTS_FOUND'
  | 'BILLING_ADDR_MISSING'
  | 'BORDEREAU_ASSIGNED'
  | 'DISCOUNT_APPLIED'
  | 'INVOICE_DELETED'
  | 'ORDER_CANCELED'
  | 'ORDER_DELETED'
  | 'ORDER_DUPLICATED'
  | 'ORDER_NOT_FOUND'
  | 'ORDER_PROCESSED'
  | 'ORDER_PRODUCTS_PICK_ASSIGNED'
  | 'ORDER_SAVED'
  | 'ORDER_SPLITTED'
  | 'ORDER_NOT_SAVED'
  | 'LABELS_DOWNLOADED'
  | 'LABELS_NOT_DOWNLOADED'
  | 'LABEL_NOT_FOUND'
  | 'LOT_QTY_UNAVAILABLE'
  | 'NEGATIVE_AMOUNTS'
  | 'DDT_PRINTED'
  | 'PRODUCT_PICKED'
  | 'PACK_ASSIGNED'
  | 'PAYMENT_NOT_SET'
  | 'PICK_ASSIGNED'
  | 'PICK_MISSING'
  | 'PICKED_MORE_THAN_ORDERED'
  | 'QTY_CANNOT_BE_ZERO'
  | 'QTY_GREATER_THAN_ON_HAND'
  | 'RESET_ORDER_PICKED'
  | 'RESET_ORDER_SUCCESS'
  | 'ROWS_MISSING'
  | 'SHIPPING_ADDR_MISSING'
  | 'SHIPMENT_CANCELED'
  | 'SHIPMENT_MISSING'
  | 'SHIPMENT_NOT_FOUND'
  | 'SHIPMENT_REQUESTED'
  | 'SKU_NOT_VALID'
  | 'ORDER_PRINTED'
  | 'PACKAGE_NOT_FOUND'
  | 'PACKAGE_NOT_AVAILABLE'
  | 'PACKAGE_ADDED'
  | 'PACKAGE_DISABLED'
  | 'TAGS_SET'
  | 'WRONG_STATUS_DELETE'

export type OrderModalCode =
  | 'CHANGE_STATUS'
  | 'COD_AMOUNT'
  | 'DELETE_ROWS'
  | 'DUPLICATE_ORDER'
  | 'INVOICES_DELETION'
  | 'ORDER_CANCEL'
  | 'ORDER_DELETION'
  | 'PICKING_IN_PROGRESS'
  | 'SHIPMENT_CANCEL'

/**
 * Parse order code to a notification
 * @param code - the order notification code
 * @returns the notification options
 */
export function parseOrderNotification(
  code?: OrderNotificationCode,
): NotificationOptions {
  switch (code) {
    case 'ADDRESS_UPDATED':
      return {
        message: 'Address updated successfully',
        color: 'success',
      }
    case 'ATTACHMENTS_FOUND':
      return {
        message: 'Orders with attachments cannot be deleted',
        color: 'error',
      }
    case 'BILLING_ADDR_MISSING':
      return {
        message: 'Billing address missing',
        color: 'error',
      }
    case 'BORDEREAU_ASSIGNED':
      return {
        message: 'Orders assigned to a bordereau cannot be processed',
        color: 'error',
      }
    case 'DISCOUNT_APPLIED':
      return {
        message: 'Discount applied',
        color: 'success',
      }
    case 'INVOICE_DELETED':
      return {
        message: 'Invoice deleted successfully',
        color: 'success',
      }
    case 'ORDER_CANCELED':
      return {
        message: 'Order canceled successfully',
        color: 'success',
      }
    case 'ATTACHMENT_DELETED':
      return {
        message: 'Attachment deleted successfully',
        color: 'success',
      }
    case 'ATTACHMENT_UPLOADED':
      return {
        message: 'Attachment uploaded successfully',
        color: 'success',
      }
    case 'DDT_PRINTED':
      return {
        message: 'DDT printed successfully',
        color: 'success',
      }
    case 'PRODUCT_PICKED':
      return {
        message: 'Product picked successfully',
        color: 'success',
      }
    case 'PACK_ASSIGNED':
      return {
        message:
          'Order assigned to unfinished packing lists cannot be modified',
        color: 'error',
      }
    case 'PAYMENT_NOT_SET':
      return {
        message: 'There was an error setting the selected payment',
        color: 'error',
      }
    case 'ROWS_MISSING':
      return {
        message: 'No rows. Add products to this order',
        color: 'error',
      }
    case 'LABELS_DOWNLOADED':
      return {
        message: 'Labels downloaded successfully',
        color: 'success',
      }
    case 'LABELS_NOT_DOWNLOADED':
      return {
        message:
          'There was an error trying to download some of the labels of this order',
        color: 'error',
      }
    case 'LABEL_NOT_FOUND':
      return {
        message: 'No label found for this order',
        color: 'error',
      }
    case 'LOT_QTY_UNAVAILABLE':
      return {
        message:
          "It's not possible to pick a quantity greater than the one available for this batch",
        color: 'error',
      }
    case 'NEGATIVE_AMOUNTS':
      return {
        message: 'One or more values are negative',
        color: 'error',
      }
    case 'ORDER_DELETED':
      return {
        message: 'Order deleted successfully',
        color: 'success',
      }
    case 'ORDER_DUPLICATED':
      return {
        message: 'Order duplicated successfully',
        color: 'success',
      }
    case 'ORDER_NOT_FOUND':
      return {
        message: 'Order not found',
        color: 'error',
      }
    case 'ORDER_NOT_SAVED': {
      return {
        message: 'An error occurred while saving this order',
        color: 'error',
      }
    }
    case 'ORDER_PROCESSED':
      return {
        message: 'Order processed successfully',
        color: 'success',
      }
    case 'ORDER_PRODUCTS_PICK_ASSIGNED':
      return {
        message: 'Orders assigned to a picking list cannot have products added',
        color: 'error',
      }
    case 'ORDER_PRINTED': {
      return {
        message: 'Order printed successfully',
        color: 'success',
      }
    }
    case 'ORDER_SAVED':
      return {
        message: 'Order saved successfully',
        color: 'success',
      }
    case 'ORDER_SPLITTED':
      return {
        message: 'Order splitted successfully',
        color: 'success',
      }
    case 'PACKAGE_NOT_FOUND':
      return {
        message: 'Package not found',
        color: 'error',
      }
    case 'PACKAGE_ADDED':
      return { message: 'Package added', color: 'success' }
    case 'PACKAGE_DISABLED':
      return {
        message:
          'This package is disabled. You need to enable it in order to use it',
        color: 'error',
      }
    case 'PACKAGE_NOT_AVAILABLE':
      return { message: 'Package not available', color: 'error' }
    case 'PICK_ASSIGNED':
      return {
        message: 'Orders assigned to a picking list cannot be processed',
        color: 'error',
      }
    case 'PICK_MISSING':
      return {
        message:
          'At least one product need to be picked in order to process this order',
        color: 'error',
      }
    case 'PICKED_MORE_THAN_ORDERED':
      return {
        message: "It's not possible to pick more than the ordered quantity",
        color: 'error',
      }
    case 'QTY_CANNOT_BE_ZERO':
      return {
        message: 'Insert a quantity greater than 0',
        color: 'error',
      }
    case 'QTY_GREATER_THAN_ON_HAND':
      return {
        message: "It's not possible to pick more than the on hand quantity",
        color: 'error',
      }
    case 'RESET_ORDER_PICKED':
      return {
        message:
          'For orders assigned to a picking list, the picking cannot be canceled',
        color: 'error',
      }
    case 'RESET_ORDER_SUCCESS':
      return {
        message: 'Order picking reset successfully',
        color: 'success',
      }
    case 'SHIPPING_ADDR_MISSING':
      return {
        message: 'Shipping address missing',
        color: 'error',
      }
    case 'SHIPMENT_CANCELED':
      return {
        message: 'Shipment canceled successfully',
        color: 'success',
      }
    case 'SHIPMENT_MISSING':
      return {
        message: 'No shipment in SHIPPED or REQUESTED status has been found',
        color: 'error',
      }
    case 'SHIPMENT_NOT_FOUND':
      return {
        message: 'Shipment not found',
        color: 'error',
      }
    case 'SHIPMENT_REQUESTED':
      return {
        message: 'Shipment requested successfully',
        color: 'success',
      }
    case 'TAGS_SET':
      return {
        message: 'Tags set successfully',
        color: 'success',
      }
    case 'WRONG_STATUS_DELETE':
      return {
        message: 'Only orders in drafted status can be deleted',
        color: 'error',
      }
    default:
      return parseGenericNotification(code as GenericNotificationCode)
  }
}

/**
 * Parse order code to an modal
 * @param code - the order modal code
 * @param order - the order object
 * @returns the modal settings
 */
export function parseOrderModal(
  code: OrderModalCode,
  order?: Order,
  status?: string,
): DialogOptions {
  switch (code) {
    case 'CHANGE_STATUS': {
      return {
        title: 'Change status',
        message: 'Are you sure you want to change the order status to %s?',
        messageParams: [status ? status : ''],
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'COD_AMOUNT': {
      return {
        title: 'Cash on delivery amounts error',
        message:
          'The cash on delivery amount (%s) is different from the order total amount (%s). Proceed?',
        messageParams: [
          order?.header?.cashOnDelivery?.amount.toFixed(2) ?? 0,
          order?.header.totalOrderAmount.toFixed(2) ?? 0,
        ],
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'DELETE_ROWS': {
      return {
        title: 'Delete rows',
        message: 'Are you sure you want to delete all rows from this order?',
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'DUPLICATE_ORDER': {
      const orderNum = order?.header.orderNumber
      return {
        title: 'Delete order',
        message:
          'You are about to duplicate the order <strong>%s</strong>. Proceed?',
        messageParams: orderNum ? [orderNum] : undefined,
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'INVOICES_DELETION': {
      return {
        title: 'Invoices found',
        message:
          'At least one invoice was found for this order that is about to be canceled. Do you want to delete all invoices?',
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'ORDER_CANCEL': {
      return {
        title: 'Cancel order',
        message:
          'The order will be permanently cancelled and it will no longer be possible to process it or make any type of modification',
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'ORDER_DELETION': {
      const orderNum = order?.header.orderNumber
      return {
        title: 'Delete order',
        message:
          'You are about to delete the order <strong>%s</strong>. Proceed with deletion?',
        messageParams: orderNum ? [orderNum] : undefined,
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'PICKING_IN_PROGRESS': {
      return {
        title: 'Order already being picked',
        message:
          'The order alert was enabled, but this order is already being managed. If you want to stop the order, contact the customer service. Proceed?',
        buttons: ['Cancel', 'Confirm'],
      }
    }
    case 'SHIPMENT_CANCEL': {
      return {
        title: 'Cancel shipment',
        message:
          'By canceling the shipment, this order won\'t be put in "Processed" status. You will have to click on "Request shipment" in order to generate another label',
        buttons: ['Cancel', 'Confirm'],
      }
    }
    default:
      return {}
  }
}
